"use client";

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { ArrowUpDown, ArrowUp, ArrowDown, LineChartIcon, TableIcon } from "lucide-react";
import { TIMELINE_COLORS } from "../constants";
import type { ActivityTimelinePoint } from "../types";

type SortKey = keyof ActivityTimelinePoint;
type SortDir = "asc" | "desc";

interface ActivityTimelineChartProps {
  data: ActivityTimelinePoint[];
}

const chartConfig = {
  inbound: { label: "Inbound", color: TIMELINE_COLORS.inbound },
  sent: { label: "Auto-Sent", color: TIMELINE_COLORS.sent },
  drafts: { label: "Drafts", color: TIMELINE_COLORS.drafts },
  handovers: { label: "Escalations", color: TIMELINE_COLORS.handovers },
  errors: { label: "Errors", color: TIMELINE_COLORS.errors },
};

function formatDateTick(dateStr: string): string {
  // dateStr is YYYY-MM-DD already in user's timezone — parse as local, not UTC
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateCell(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ChartView({ data }: { data: ActivityTimelinePoint[] }) {
  const tickInterval = data.length > 60 ? 13 : data.length > 14 ? 6 : 1;

  return (
    <ChartContainer config={chartConfig} className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            interval={tickInterval}
            fontSize={11}
          />
          <YAxis fontSize={12} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="inbound"
            stroke={TIMELINE_COLORS.inbound}
            strokeWidth={2}
            dot={false}
            name="Inbound"
          />
          <Line
            type="monotone"
            dataKey="sent"
            stroke={TIMELINE_COLORS.sent}
            strokeWidth={2}
            dot={false}
            name="Auto-Sent"
          />
          <Line
            type="monotone"
            dataKey="drafts"
            stroke={TIMELINE_COLORS.drafts}
            strokeWidth={1.5}
            dot={false}
            name="Drafts"
          />
          <Line
            type="monotone"
            dataKey="handovers"
            stroke={TIMELINE_COLORS.handovers}
            strokeWidth={1.5}
            dot={false}
            name="Escalations"
          />
          <Line
            type="monotone"
            dataKey="errors"
            stroke={TIMELINE_COLORS.errors}
            strokeWidth={1.5}
            dot={false}
            name="Errors"
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

const COLUMNS: { key: SortKey; label: string; color?: string }[] = [
  { key: "date", label: "Date" },
  { key: "inbound", label: "Inbound", color: TIMELINE_COLORS.inbound },
  { key: "sent", label: "Auto-Sent", color: TIMELINE_COLORS.sent },
  { key: "drafts", label: "Drafts", color: TIMELINE_COLORS.drafts },
  { key: "handovers", label: "Escalations", color: TIMELINE_COLORS.handovers },
  { key: "errors", label: "Errors", color: TIMELINE_COLORS.errors },
];

function SortIcon({ column, sortKey, sortDir }: { column: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (column !== sortKey) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return sortDir === "asc"
    ? <ArrowUp className="w-3 h-3" />
    : <ArrowDown className="w-3 h-3" />;
}

function TableView({ data }: { data: ActivityTimelinePoint[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "desc");
    }
  }

  return (
    <div className="-mx-6 -mb-6 max-h-[450px] overflow-auto">
      <table className="w-full caption-bottom text-xs sm:text-sm [&_td]:px-2 [&_td]:py-2 sm:[&_td]:p-4">
        <thead className="sticky top-0 z-10 bg-muted [&_tr]:border-b">
          <tr className="border-b">
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`h-10 whitespace-nowrap px-2 align-middle font-medium text-muted-foreground select-none cursor-pointer hover:text-foreground transition-colors sm:h-12 sm:px-4 ${
                  col.key === "date" ? "text-left" : "text-right"
                }`}
                onClick={() => toggleSort(col.key)}
              >
                <span className={`inline-flex items-center gap-1 ${col.key !== "date" ? "justify-end" : ""}`}>
                  {col.label}
                  <SortIcon column={col.key} sortKey={sortKey} sortDir={sortDir} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {sorted.map((row) => (
            <tr key={row.date} className="border-b transition-colors hover:bg-muted/50">
              <td className="whitespace-nowrap align-middle font-medium">
                <time dateTime={row.date} title={formatDateCell(row.date)}>
                  <span className="sm:hidden">{formatDateTick(row.date)}</span>
                  <span className="hidden sm:inline">{formatDateCell(row.date)}</span>
                </time>
              </td>
              <td className="align-middle text-right tabular-nums" style={{ color: TIMELINE_COLORS.inbound }}>
                {row.inbound.toLocaleString()}
              </td>
              <td className="align-middle text-right tabular-nums" style={{ color: TIMELINE_COLORS.sent }}>
                {row.sent.toLocaleString()}
              </td>
              <td className="align-middle text-right tabular-nums" style={{ color: TIMELINE_COLORS.drafts }}>
                {row.drafts.toLocaleString()}
              </td>
              <td className="align-middle text-right tabular-nums" style={{ color: TIMELINE_COLORS.handovers }}>
                {row.handovers.toLocaleString()}
              </td>
              <td className="align-middle text-right tabular-nums" style={{ color: TIMELINE_COLORS.errors }}>
                {row.errors.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ActivityTimelineChart({ data }: ActivityTimelineChartProps) {
  const [view, setView] = useState<"chart" | "table">("chart");

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">Activity Over Time</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Daily email volume and processing outcomes
            </p>
          </div>
          <div className="flex items-center border rounded-md">
            <Button
              variant={view === "chart" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2.5 rounded-r-none"
              onClick={() => setView("chart")}
              aria-label="Chart view"
              aria-pressed={view === "chart"}
            >
              <LineChartIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={view === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2.5 rounded-l-none"
              onClick={() => setView("table")}
              aria-label="Table view"
              aria-pressed={view === "table"}
            >
              <TableIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div
            className={`transition-all duration-300 ease-in-out ${
              view === "chart"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 absolute inset-0 pointer-events-none"
            }`}
          >
            <ChartView data={data} />
          </div>
          <div
            className={`transition-all duration-300 ease-in-out ${
              view === "table"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 absolute inset-0 pointer-events-none"
            }`}
          >
            <TableView data={data} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
